import { Fragment } from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

export interface WizardStep {
  id: string;
  label: string;
  // Optional transient sub-label, e.g. "generating…" on the chat step while a
  // response streams in the background. Not part of the step's identity.
  hint?: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  activeIndex: number;
  completedIndices: number[];
  onStepClick?: (index: number) => void;
}

const WizardStepper = ({ steps, activeIndex, completedIndices, onStepClick }: WizardStepperProps) => {
  const completed = new Set(completedIndices);

  return (
    <div className="flex items-start justify-center py-6">
      {steps.map((step, idx) => {
        const isCompleted = completed.has(idx);
        const isActive = idx === activeIndex;
        const isLast = idx === steps.length - 1;
        const isClickable = !!onStepClick;

        const circleBase =
          "flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm font-semibold transition-colors";
        let circleClasses: string;
        if (isCompleted) {
          circleClasses = "bg-brand-teal border-brand-teal text-white";
        } else if (isActive) {
          circleClasses = "bg-brand-teal border-brand-teal text-white ring-4 ring-brand-teal/15";
        } else {
          circleClasses = "bg-background border-muted-foreground/30 text-muted-foreground";
        }

        return (
          <Fragment key={step.id}>
            <div className="flex flex-col items-center w-24 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  if (idx !== activeIndex) onStepClick?.(idx);
                }}
                disabled={!isClickable}
                className={cn(
                  circleBase,
                  circleClasses,
                  isClickable && "cursor-pointer hover:opacity-90",
                  !isClickable && "cursor-default",
                )}
                aria-label={`Step ${idx + 1}: ${step.label}`}
                aria-current={isActive ? "step" : undefined}
              >
                {isCompleted ? <Check className="w-5 h-5" /> : idx + 1}
              </button>
              <span
                className={cn(
                  "mt-2 text-xs text-center font-medium leading-tight",
                  isActive ? "text-brand-navy" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
              {step.hint && (
                <span className="mt-0.5 text-center text-[10px] leading-tight text-brand-teal">
                  {step.hint}
                </span>
              )}
            </div>
            {!isLast && (
              <div
                className={cn(
                  "h-0.5 w-24 mt-5 flex-shrink-0",
                  isCompleted ? "bg-brand-teal" : "bg-muted-foreground/20",
                )}
              />
            )}
          </Fragment>
        );
      })}
    </div>
  );
};

export default WizardStepper;
